import { useEffect, useRef, useMemo, useCallback } from "react";
import { getBoardItems } from "../services/mondayApiService";
import { BOARD_CONFIG } from "../constants/boardConfig";
import {
  LAST_COMPLETED_DATE_COLUMN_ID,
  METER_READINGS_LAST_COMPLETED_DATE_COLUMN_ID,
  TASKS_DESCRIPTION_COLUMN_ID,
  TIME_TRACKING_COLUMN_ID,
  PROPERTIES_BOARD_ID,
  WEEKLY_CHECKS_BOARD_ID,
  METER_READINGS_BOARD_ID,
  TASKS_BOARD_ID,
  CLEANING_CHECKS_BOARD_ID,
  BOOKINGS_BOARD_ID,
} from "../constants/columnIds";
import {
  normalizeDateOnly,
  isDateMatch,
  isDateInWeek,
  isDateWithinWindow,
  isDateWindowInWeek,
} from "../utils/dateUtils";
import { parseColumnValue, parseLookupDateColumn } from "../utils/dataParsingUtils";
import { createTaskEntry, addTaskToGroup, matchesPersonFilter } from "../helpers/taskProcessing";
import { useMondayContext } from "./useMondayContext";

/**
 * useTasks - Hook to fetch and manage tasks data
 * Implements caching: checks context for rawItems first, only fetches if not cached
 * Processes cached rawItems based on view parameters (date, person filter, etc.)
 */
export function useTasks({
  viewType,
  currentDate,
  currentWeekStart,
  selectedPerson,
  personColumnId,
  personColumnIdMap,
  config,
  boardId,
}) {
  const {
    rawItems,
    rawItemsFetched,
    tasksByProperty,
    propertyNameToIdMap,
    tasksLoading,
    tasksError,
    updateRawItems,
    resetRawItemsCache,
    updateTasksByProperty,
    updatePropertyNameToIdMap,
    setTasksLoadingState,
    setTasksErrorState,
    availablePeople,
    updateAvailablePeople,
    availableProperties, // Get cached properties to supplement linked items
  } = useMondayContext();

  const isFetchingRawItems = useRef(false);

  // Memoize people extraction from raw items (only recalculates when rawItems or personColumnIdMap changes)
  const extractedPeople = useMemo(() => {
    if (!rawItems || rawItems.length === 0) {
      return [];
    }

    const peopleSet = new Set();
    
    // Extract from all task items
    for (const item of rawItems) {
      const columnValues = item.column_values || [];
      // Get the board-specific person column ID
      const boardPersonColumnId = personColumnIdMap[item._boardId] || personColumnId;
      
      if (boardPersonColumnId) {
        const personCol = columnValues.find((c) => c.id === boardPersonColumnId);
        if (personCol) {
          // For dropdown columns, try multiple approaches to extract values
          let labels = [];
          
          // First, check the text field (Monday.com provides human-readable text)
          if (personCol.text) {
            // Text field may contain comma-separated or newline-separated values
            const textLabels = personCol.text.split(/[,\n]/).map((l) => l.trim()).filter(Boolean);
            if (textLabels.length > 0) {
              labels = textLabels;
            }
          }
          
          // Also try parsing the value JSON (Monday.com dropdown structure) as backup
          if (labels.length === 0 && personCol.value && personCol.value !== "null" && personCol.value !== "") {
            try {
              const parsed = JSON.parse(personCol.value);
              // Monday.com dropdown can have: { labels: [...] } or { ids: [...], labels: [...] }
              if (parsed.labels && Array.isArray(parsed.labels)) {
                labels = parsed.labels;
              } else if (Array.isArray(parsed)) {
                // Sometimes it's just an array of labels
                labels = parsed;
              } else if (parsed.label) {
                // Single label object
                labels = [parsed.label];
              } else if (typeof parsed === 'string') {
                // Single label as string
                labels = [parsed];
              }
            } catch (e) {
              // Continue - text field is primary source
            }
          }
          
          // Add all labels to the set
          labels.forEach((label) => {
            if (label) {
              // Handle both string labels and objects with label property
              const labelText = typeof label === 'string' ? label : (label.label || label.name || String(label));
              if (labelText && typeof labelText === 'string' && labelText.trim()) {
                peopleSet.add(labelText.trim());
              }
            }
          });
        }
      }
    }
    
    return Array.from(peopleSet);
  }, [rawItems, personColumnIdMap, personColumnId]);

  // Memoize merged people list (only recalculates when extractedPeople or availablePeople changes)
  // IMPORTANT: Dropdown options (availablePeople) contain ALL available people from the dropdown settings
  // We should prioritize dropdown options and only supplement with people from tasks if needed
  const mergedPeople = useMemo(() => {
    const mergedSet = new Set();
    
    // Always start with dropdown options (these contain ALL available people)
    // availablePeople comes from dropdown column settings fetched in usePersonColumn
    availablePeople.forEach((person) => {
      mergedSet.add(person.name || person.id);
    });
    
    // Add people extracted from tasks as well (in case there are any new ones not in dropdown)
    // This should be minimal since dropdown settings should have all options
    extractedPeople.forEach((name) => {
      mergedSet.add(name);
    });
    
    // Convert to array and sort
    return Array.from(mergedSet)
      .map((name) => ({ id: name, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [extractedPeople, availablePeople]);

  // Memoize task processing (only recalculates when view params or rawItems change)
  const processedTasksData = useMemo(() => {
    if (!rawItemsFetched || !rawItems || rawItems.length === 0) {
      return { tasksByProperty: {}, propertyNameToIdMap: {} };
    }

    const items = rawItems; // Use cached raw items
    const weeklyChecksBoardId = WEEKLY_CHECKS_BOARD_ID;
    const meterReadingsBoardId = METER_READINGS_BOARD_ID;
    const cleaningChecksBoardId = CLEANING_CHECKS_BOARD_ID;
    const lastCompletedDateColumnId = LAST_COMPLETED_DATE_COLUMN_ID;
    const meterReadingsLastCompletedDateColumnId = METER_READINGS_LAST_COMPLETED_DATE_COLUMN_ID;

    const tasksByPropertyLocal = {};
    const targetDate = viewType === "daily" ? currentDate : currentWeekStart;

    // Create a map of property name -> property ID (for Key # lookup)
    const propertyNameToIdMapLocal = {};
    
    // Create a map of property ID -> property name (for reverse lookup)
    // This is used to map property IDs from BOOKINGS to property names
    const propertyIdToNameMap = {};
    
    // Build property ID -> name map from cached properties first (more reliable and avoids redundant fetches)
    if (availableProperties && availableProperties.length > 0) {
      availableProperties.forEach((prop) => {
        if (prop.id && prop.name) {
          propertyIdToNameMap[prop.id] = prop.name;
          // Also populate propertyNameToIdMapLocal for Key # lookup
          if (!propertyNameToIdMapLocal[prop.name]) {
            propertyNameToIdMapLocal[prop.name] = prop.id;
          }
        }
      });
    }

    for (const item of items) {
      // Get the config for this item's board
      const itemConfig = item._config || config;
      if (!itemConfig) continue; // Skip if no config available
      
      const columnValues = item.column_values || [];

      // Get property names from linked_items (board relation)
      const linkedItems = item.linked_items || [];
      // Extract property names and IDs from linked items, using cached properties as fallback
      const propertyInfos = linkedItems.length > 0
        ? linkedItems.map((li) => {
            // Use cached property name if available, otherwise use linked item name
            const cachedName = propertyIdToNameMap[li.id] || li.name;
            return { name: cachedName, id: li.id };
          }).filter(p => p.name)
        : [{ name: "(No property)", id: null }];
      
      const propertyNames = propertyInfos.map(p => p.name);
      
      // Store property name -> ID mapping for Key # lookup (supplement with linked items)
      propertyInfos.forEach((prop) => {
        if (prop.name && prop.id && !propertyNameToIdMapLocal[prop.name]) {
          propertyNameToIdMapLocal[prop.name] = prop.id;
        }
      });
      
      // Also update propertyIdToNameMap from linked items (in case some properties aren't in cache)
      linkedItems.forEach((li) => {
        if (li.id && li.name && !propertyIdToNameMap[li.id]) {
          propertyIdToNameMap[li.id] = li.name;
        }
      });
      
      // Extract property item IDs
      let propertyItemIds = [];
      
      if (linkedItems && linkedItems.length > 0) {
        propertyItemIds = linkedItems
          .map((li) => li.id)
          .filter(id => id != null && id !== '');
      }
      
      if (propertyItemIds.length === 0) {
        const propertyCol = columnValues.find((c) => c.id === itemConfig.propertyColumnId);
        if (propertyCol && propertyCol.value && propertyCol.value !== "null" && propertyCol.value !== "") {
          try {
            const parsed = JSON.parse(propertyCol.value);
            if (parsed.linked_item_ids && Array.isArray(parsed.linked_item_ids)) {
              propertyItemIds = parsed.linked_item_ids
                .map(id => typeof id === 'string' ? parseInt(id, 10) : Number(id))
                .filter(id => !isNaN(id) && id != null);
            }
          } catch (e) {
            // Failed to parse column value
          }
        }
      }
      
      // Get the board-specific person column ID
      const boardPersonColumnId = personColumnIdMap[item._boardId] || personColumnId;
      
      // Parse person data from dropdown column
      const personText = boardPersonColumnId
        ? parseColumnValue(columnValues, boardPersonColumnId)
        : null;
      
      let personNames = [];
      if (boardPersonColumnId) {
        const personCol = columnValues.find((c) => c.id === boardPersonColumnId);
        if (personCol) {
          const allLabels = new Set();
          
          if (personCol.text) {
            const textLabels = personCol.text.split(/[,\n]/).map((l) => l.trim()).filter(Boolean);
            textLabels.forEach(label => allLabels.add(label));
          }
          
          if (personCol.value && personCol.value !== "null" && personCol.value !== "") {
            try {
              const parsed = JSON.parse(personCol.value);
              if (parsed.labels && Array.isArray(parsed.labels)) {
                parsed.labels.forEach((label) => {
                  const labelStr = typeof label === 'string' ? label : String(label);
                  const trimmed = labelStr.trim();
                  if (trimmed) allLabels.add(trimmed);
                });
              } else if (Array.isArray(parsed)) {
                parsed.forEach((label) => {
                  const labelStr = typeof label === 'string' ? label : String(label);
                  const trimmed = labelStr.trim();
                  if (trimmed) allLabels.add(trimmed);
                });
              } else if (parsed.label) {
                const labelStr = typeof parsed.label === 'string' ? parsed.label : String(parsed.label);
                const trimmed = labelStr.trim();
                if (trimmed) allLabels.add(trimmed);
              }
            } catch (e) {
              // If not JSON, text field is primary source
            }
          }
          
          personNames = Array.from(allLabels);
        }
      }

      // Special handling for Cleaning Checks board - create two tasks per item
      if (item._boardId === cleaningChecksBoardId) {
        // For Cleaning Checks, property column is a mirror column (like dates), so it returns null
        // We need to extract property directly from linked BOOKINGS item's property column
        // This is the same way other boards get properties - from linked items' property columns
        let cleaningChecksPropertyNames = [];
        let cleaningChecksPropertyItemIds = [];
        
        // Extract property from linked BOOKINGS item's property column
        // BOOKINGS items have a property column (board_relation_mkyabpt4) that links to Properties board
        if (item.linked_items && item.linked_items.length > 0) {
          const linkedBookingItem = item.linked_items[0];
          
          // First, try to get property from linked property items (nested linked_items)
          // The BOOKINGS item's property column links to Properties, and those should be in linked_items
          if (linkedBookingItem.linked_items && linkedBookingItem.linked_items.length > 0) {
            // These are the linked Properties items
            const propertyInfos = linkedBookingItem.linked_items
              .map((li) => ({ name: li.name, id: li.id }))
              .filter(p => p.name);
            
            if (propertyInfos.length > 0) {
              cleaningChecksPropertyNames = propertyInfos.map(p => p.name);
              cleaningChecksPropertyItemIds = propertyInfos
                .map(p => p.id)
                .filter(id => id != null && id !== '')
                .map(id => typeof id === 'string' ? parseInt(id, 10) : Number(id))
                .filter(id => !isNaN(id) && id != null);
            }
          }
          
          // Fallback: try to get property from property column value/text
          if (cleaningChecksPropertyNames.length === 0 && linkedBookingItem.column_values && linkedBookingItem.column_values.length > 0) {
            // Find the specific property column in BOOKINGS using the column ID
            const propertyCol = linkedBookingItem.column_values.find(
              col => col.id === itemConfig.bookingsPropertyColumnId
            );
            
            if (propertyCol && propertyCol.type === 'board_relation') {
              // Extract property names and IDs from the property column
              // First try to get property names from the text (most reliable)
              const propertyNameFromText = propertyCol.text?.trim();
              if (propertyNameFromText) {
                cleaningChecksPropertyNames = propertyNameFromText.split(',').map(p => p.trim()).filter(Boolean);
              }
              
              // Extract property item IDs from the value
              // The value should contain JSON with linked_item_ids array
              if (propertyCol.value && propertyCol.value !== "null" && propertyCol.value !== "" && propertyCol.value !== null) {
                try {
                  const parsed = JSON.parse(propertyCol.value);
                  if (parsed.linked_item_ids && Array.isArray(parsed.linked_item_ids) && parsed.linked_item_ids.length > 0) {
                    // Extract property item IDs - handle both string and number IDs
                    cleaningChecksPropertyItemIds = parsed.linked_item_ids
                      .map(id => {
                        if (typeof id === 'number') return id;
                        if (typeof id === 'string') {
                          const numId = parseInt(id, 10);
                          return !isNaN(numId) ? numId : null;
                        }
                        return null;
                      })
                      .filter(id => id != null);
                  }
                } catch (e) {
                  // Failed to parse - property IDs will remain empty
                }
              }
              
              // If we have property names but no IDs, that's okay - we'll use the names
              // If we have IDs but no names, try to get names from text
              if (cleaningChecksPropertyNames.length === 0 && propertyCol.text && propertyCol.text.trim() !== "") {
                cleaningChecksPropertyNames = [propertyCol.text.trim()];
              }
            }
          }
        }
        
        // Final fallback: use default
        if (cleaningChecksPropertyNames.length === 0) {
          cleaningChecksPropertyNames = ["(No property)"];
        }
        
        // Store property name -> ID mapping for Key # lookup (same as other boards)
        if (cleaningChecksPropertyNames.length > 0 && cleaningChecksPropertyNames[0] !== "(No property)") {
          // Update propertyItemIds with the extracted property IDs
          propertyItemIds = cleaningChecksPropertyItemIds;
          
          // Store property name -> ID mapping for Key # lookup
          cleaningChecksPropertyNames.forEach((propName, index) => {
            if (cleaningChecksPropertyItemIds[index] && !propertyNameToIdMapLocal[propName]) {
              propertyNameToIdMapLocal[propName] = cleaningChecksPropertyItemIds[index];
            }
          });
        }

        // Parse check-in and check-out dates
        // Mirror columns mirror dates from linked BOOKINGS items
        // First try to get dates from mirror columns
        let checkInDateText = parseLookupDateColumn(columnValues, itemConfig.checkInDateColumnId);
        let checkOutDateText = parseLookupDateColumn(columnValues, itemConfig.checkOutDateColumnId);
        
        // If mirror columns are null, try to extract dates from linked BOOKINGS items
        if ((!checkInDateText || !checkOutDateText) && item.linked_items && item.linked_items.length > 0) {
          // Get the first linked item (BOOKINGS item)
          const linkedBookingItem = item.linked_items[0];
          
          // Try to find date columns in the linked item's column values
          if (linkedBookingItem.column_values && linkedBookingItem.column_values.length > 0) {
            // Look for date columns in the linked item
            // We need to find which columns are the check-in and check-out dates in the BOOKINGS board
            // For now, try to find any date columns and use them
            for (const linkedCol of linkedBookingItem.column_values) {
              if (linkedCol.type === 'date' || linkedCol.type === 'mirror') {
                const dateValue = parseLookupDateColumn(linkedBookingItem.column_values, linkedCol.id) || 
                                  parseColumnValue(linkedBookingItem.column_values, linkedCol.id);
                if (dateValue && dateValue.match(/^\d{4}-\d{2}-\d{2}/)) {
                  // If we don't have check-in date yet, use this one
                  if (!checkInDateText) {
                    checkInDateText = dateValue;
                  } 
                  // If we have check-in but not check-out, use this one
                  else if (!checkOutDateText) {
                    checkOutDateText = dateValue;
                  }
                }
              }
            }
          }
        }
        
        // Fallback to regular parseColumnValue if still not found
        if (!checkInDateText) {
          checkInDateText = parseColumnValue(columnValues, itemConfig.checkInDateColumnId);
        }
        if (!checkOutDateText) {
          checkOutDateText = parseColumnValue(columnValues, itemConfig.checkOutDateColumnId);
        }
        
        // Parse extras
        const extras = parseColumnValue(columnValues, itemConfig.extrasColumnId) || "";

        // Create check-in task if date is relevant
        if (checkInDateText && checkInDateText.trim() !== "" && checkInDateText.toLowerCase() !== "no date") {
          let checkInDateRelevant = false;
          let checkInTaskDate = null;
          
          if (viewType === "daily") {
            checkInDateRelevant = isDateMatch(checkInDateText, targetDate);
            checkInTaskDate = normalizeDateOnly(checkInDateText);
          } else {
            checkInDateRelevant = isDateInWeek(checkInDateText, targetDate);
            checkInTaskDate = normalizeDateOnly(checkInDateText);
          }

          if (checkInDateRelevant && matchesPersonFilter(personNames, selectedPerson, item)) {
            const checkInTaskData = createTaskEntry(
              item,
              columnValues,
              cleaningChecksPropertyNames,
              personNames,
              personText,
              checkInTaskDate,
              false,
              propertyItemIds,
              itemConfig,
              null,
              null,
              config,
              boardId,
              "Check-in",
              extras,
              itemConfig.checkInStatusColumnId
            );
            addTaskToGroup(checkInTaskData, cleaningChecksPropertyNames, checkInTaskDate, viewType, tasksByPropertyLocal);
          }
        }

        // Create check-out task if date is relevant
        if (checkOutDateText && checkOutDateText.trim() !== "" && checkOutDateText.toLowerCase() !== "no date") {
          let checkOutDateRelevant = false;
          let checkOutTaskDate = null;
          
          if (viewType === "daily") {
            checkOutDateRelevant = isDateMatch(checkOutDateText, targetDate);
            checkOutTaskDate = normalizeDateOnly(checkOutDateText);
          } else {
            checkOutDateRelevant = isDateInWeek(checkOutDateText, targetDate);
            checkOutTaskDate = normalizeDateOnly(checkOutDateText);
          }

          if (checkOutDateRelevant && matchesPersonFilter(personNames, selectedPerson, item)) {
            const checkOutTaskData = createTaskEntry(
              item,
              columnValues,
              cleaningChecksPropertyNames,
              personNames,
              personText,
              checkOutTaskDate,
              false,
              propertyItemIds,
              itemConfig,
              null,
              null,
              config,
              boardId,
              "Check-out",
              extras,
              itemConfig.checkOutStatusColumnId
            );
            addTaskToGroup(checkOutTaskData, cleaningChecksPropertyNames, checkOutTaskDate, viewType, tasksByPropertyLocal);
          }
        }

        // Skip the regular task creation logic for Cleaning Checks
        continue;
      }

      // Parse last completed date (for Weekly Checks and Meter Readings boards)
      let lastCompletedDateText = null;
      let lastCompletedDate = null;
      if (item._boardId === weeklyChecksBoardId) {
        lastCompletedDateText = parseColumnValue(
          columnValues,
          lastCompletedDateColumnId
        );
        lastCompletedDate = lastCompletedDateText ? normalizeDateOnly(lastCompletedDateText) : null;
      } else if (item._boardId === meterReadingsBoardId) {
        lastCompletedDateText = parseColumnValue(
          columnValues,
          meterReadingsLastCompletedDateColumnId
        );
        lastCompletedDate = lastCompletedDateText ? normalizeDateOnly(lastCompletedDateText) : null;
      }

      // Check if task is relevant for the due date
      let dueDateRelevant = false;
      let dueTaskDate = null;

      if (itemConfig.dueDateColumnId && itemConfig.startDateColumnId && itemConfig.endDateColumnId) {
        // Meter Readings - use dueDateColumnId as the Due Date
        // The window (startDateColumnId and endDateColumnId) is only used for automatic date assignment, not for display
        const dueDateText = parseColumnValue(columnValues, itemConfig.dueDateColumnId);
        
        // Check if Due Date is present and not empty
        const hasDueDate = dueDateText && dueDateText.trim() !== "" && dueDateText.toLowerCase() !== "no date";
        
        if (hasDueDate) {
          // Meter Readings should only show on the exact Due Date
          // Window logic is only for automatic date assignment, not for display
          if (viewType === "daily") {
            dueDateRelevant = isDateMatch(dueDateText, targetDate);
            dueTaskDate = normalizeDateOnly(dueDateText);
          } else {
            // Weekly view: check if Due Date is in the week
            dueDateRelevant = isDateInWeek(dueDateText, targetDate);
            dueTaskDate = normalizeDateOnly(dueDateText);
          }
        }
        // If Due Date is not present, task won't be shown (dueDateRelevant remains false)
      } else if (itemConfig.dueDateColumnId) {
        // Regular due date
        const dueText = parseColumnValue(columnValues, itemConfig.dueDateColumnId);
        if (viewType === "daily") {
          dueDateRelevant = isDateMatch(dueText, targetDate);
          dueTaskDate = normalizeDateOnly(dueText);
        } else {
          dueDateRelevant = isDateInWeek(dueText, targetDate);
          dueTaskDate = normalizeDateOnly(dueText);
        }
      }

      // Check if last completed date matches the target date
      let completedDateRelevant = false;
      if (lastCompletedDateText && lastCompletedDate) {
        if (viewType === "daily") {
          completedDateRelevant = isDateMatch(lastCompletedDateText, targetDate);
        } else {
          completedDateRelevant = isDateInWeek(lastCompletedDateText, targetDate);
        }
      }

      // For Meter Readings, if both due date and completed date match the target date,
      // only show the completed entry to avoid duplicates
      const shouldSkipDueEntry = item._boardId === meterReadingsBoardId && 
                                  dueDateRelevant && 
                                  completedDateRelevant &&
                                  dueTaskDate &&
                                  lastCompletedDate &&
                                  dueTaskDate.getTime() === lastCompletedDate.getTime();

      // Create task entry for due date if relevant AND matches person filter
      // Skip if this is a Meter Readings task where both dates match (to avoid duplicates)
      if (dueDateRelevant && !shouldSkipDueEntry && matchesPersonFilter(personNames, selectedPerson, item)) {
        const taskData = createTaskEntry(
          item,
          columnValues,
          propertyNames,
          personNames,
          personText,
          dueTaskDate,
          false,
          propertyItemIds,
          itemConfig,
          null,
          null,
          config,
          boardId
        );
        addTaskToGroup(taskData, propertyNames, dueTaskDate, viewType, tasksByPropertyLocal);
      }

      // Create separate task entry for last completed date if relevant AND matches person filter
      if (completedDateRelevant && matchesPersonFilter(personNames, selectedPerson, item)) {
        const completedTaskDate = normalizeDateOnly(lastCompletedDateText);
        const taskData = createTaskEntry(
          item,
          columnValues,
          propertyNames,
          personNames,
          personText,
          completedTaskDate,
          true, // Mark as completed entry
          propertyItemIds,
          itemConfig,
          lastCompletedDate,
          lastCompletedDateText,
          config,
          boardId
        );
        addTaskToGroup(taskData, propertyNames, completedTaskDate, viewType, tasksByPropertyLocal);
      }
    }
    
    return {
      tasksByProperty: tasksByPropertyLocal,
      propertyNameToIdMap: propertyNameToIdMapLocal,
    };
  }, [
    rawItemsFetched,
    rawItems,
    viewType,
    currentDate,
    currentWeekStart,
    selectedPerson,
    personColumnId,
    personColumnIdMap,
    config,
    boardId,
    availableProperties, // Include in dependencies to update when properties are cached
  ]);

  // Fetch raw items from API (only once per session)
  useEffect(() => {
    // Check cache first - if raw items already fetched, skip API call
    if (rawItemsFetched && rawItems.length > 0) {
      return; // Raw items already cached, no need to fetch
    }

    // Prevent duplicate fetches
    if (isFetchingRawItems.current) {
      return;
    }

    const fetchRawItems = async () => {
      isFetchingRawItems.current = true;
      setTasksLoadingState(true);
      setTasksErrorState(null);

      try {
        // Define the boards to fetch from (TASKS, Weekly Checks, Meter Readings, and Cleaning Checks)
        const boardsToFetch = [
          { id: TASKS_BOARD_ID, config: BOARD_CONFIG[TASKS_BOARD_ID] }, // TASKS
          { id: WEEKLY_CHECKS_BOARD_ID, config: BOARD_CONFIG[WEEKLY_CHECKS_BOARD_ID] }, // Weekly Checks
          { id: METER_READINGS_BOARD_ID, config: BOARD_CONFIG[METER_READINGS_BOARD_ID] }, // Meter Readings
          { id: CLEANING_CHECKS_BOARD_ID, config: BOARD_CONFIG[CLEANING_CHECKS_BOARD_ID] }, // Cleaning Checks
        ];
        
        const lastCompletedDateColumnId = LAST_COMPLETED_DATE_COLUMN_ID; // Weekly Checks
        const meterReadingsLastCompletedDateColumnId = METER_READINGS_LAST_COMPLETED_DATE_COLUMN_ID; // Meter Readings
        const tasksDescriptionColumnId = TASKS_DESCRIPTION_COLUMN_ID; // TASKS board description column
        const propertiesBoardId = PROPERTIES_BOARD_ID;
        const weeklyChecksBoardId = WEEKLY_CHECKS_BOARD_ID;
        const meterReadingsBoardId = METER_READINGS_BOARD_ID;
        const tasksBoardId = TASKS_BOARD_ID;
        const cleaningChecksBoardId = CLEANING_CHECKS_BOARD_ID;
        
        // Fetch all items from all boards in parallel for better performance
        const boardPromises = boardsToFetch
          .filter(({ config }) => config) // Filter out boards without config
          .map(async ({ id: currentBoardId, config: currentConfig }) => {
            // Get the person column ID for this specific board
            const boardPersonColumnId = personColumnIdMap[currentBoardId] || personColumnId;
            
            // Include last completed date column for Weekly Checks and Meter Readings boards
            // Include description and time tracking columns for TASKS board
            // Include Electricity, Water, and Gas columns for Meter Readings board
            const columnIds = [
              currentConfig.propertyColumnId,
              currentConfig.dueDateColumnId,
              currentConfig.startDateColumnId,
              currentConfig.endDateColumnId,
              currentConfig.statusColumnId,
              boardPersonColumnId, // Use board-specific person column ID
              // Fetch last completed date and type column for Weekly Checks board
              ...(currentBoardId === weeklyChecksBoardId ? [
                lastCompletedDateColumnId,
                currentConfig.typeColumnId, // Type column (Weekly Check / Monthly Check)
              ] : []),
              // Fetch last completed date and meter reading columns for Meter Readings board
              ...(currentBoardId === meterReadingsBoardId ? [
                meterReadingsLastCompletedDateColumnId,
                "text_mky97tgp", // ELECTRICITY_COLUMN_ID
                "file_mkyf7jcg", // WATER_COLUMN_ID
                "file_mkyf5970", // GAS_COLUMN_ID
                "text_mkyj5fvd", // METER_READINGS_NOTES_COLUMN_ID
                "date_mkzay4ke", // ELECTRICITY_DATE_COLUMN_ID
                "date_mkza464v", // WATER_DATE_COLUMN_ID
                "date_mkzac0ze", // GAS_DATE_COLUMN_ID
              ] : []),
              // Fetch description, time tracking, file, and comments columns for TASKS board
              ...(currentBoardId === tasksBoardId ? [
                tasksDescriptionColumnId,
                TIME_TRACKING_COLUMN_ID,
                "file_mkyssk15", // TASKS_FILE_COLUMN_ID
                "text_mkysxesd", // TASKS_COMMENTS_COLUMN_ID
              ] : []),
              // Fetch Cleaning Checks columns (lookup columns and extras)
              ...(currentBoardId === cleaningChecksBoardId ? [
                currentConfig.checkInDateColumnId,
                currentConfig.checkInStatusColumnId,
                currentConfig.checkOutDateColumnId,
                currentConfig.checkOutStatusColumnId,
                currentConfig.extrasColumnId,
              ] : []),
            ].filter(Boolean);

            // Fetch the task items for this board
            try {
              // For Cleaning Checks, we need to query linked items from BOOKINGS board to get the dates
              // The mirror columns mirror dates from linked BOOKINGS items via board_relation_mkybf63a
              const boards = await getBoardItems({
                boardIds: [currentBoardId],
                columnIds: columnIds,
                linkToItemColumnId: currentBoardId === cleaningChecksBoardId 
                  ? currentConfig.bookingsRelationColumnId  // Use BOOKINGS relation column for Cleaning Checks
                  : currentConfig.propertyColumnId,
                linkedBoardId: currentBoardId === cleaningChecksBoardId 
                  ? BOOKINGS_BOARD_ID  // Use BOOKINGS board ID for Cleaning Checks
                  : propertiesBoardId,
                queryAllColumns: currentBoardId === cleaningChecksBoardId, // Query all columns for Cleaning Checks
                // For Cleaning Checks, also query nested linked items (Properties from BOOKINGS)
                nestedLinkToItemColumnId: currentBoardId === cleaningChecksBoardId 
                  ? currentConfig.bookingsPropertyColumnId  // Property column in BOOKINGS board
                  : null,
                nestedLinkedBoardId: currentBoardId === cleaningChecksBoardId 
                  ? PROPERTIES_BOARD_ID  // Properties board ID
                  : null,
              });

              const board = boards?.[0];
              const items = board?.items_page?.items || [];
              
              // Add board ID and config to each item for later processing
              items.forEach(item => {
                item._boardId = currentBoardId;
                item._config = currentConfig;
              });
              
              return { currentBoardId, currentConfig, items, error: null };
            } catch (boardErr) {
              console.error(`Error fetching board ${currentBoardId} (${currentConfig.name}):`, boardErr);
              // Return error info but don't throw - allow other boards to complete
              return { currentBoardId, currentConfig, items: [], error: boardErr };
            }
          });

        // Wait for all boards to fetch in parallel
        const boardResults = await Promise.all(boardPromises);
        
        // Combine all items from all boards
        const allItems = [];
        boardResults.forEach(({ currentBoardId, currentConfig, items, error }) => {
          if (error) {
            // Error already logged, continue with other boards
            return;
          }
          allItems.push(...items);
        });

        // Store raw items in context cache (fetched once per session)
        updateRawItems(allItems);
      } catch (err) {
        console.error("Error fetching raw items:", err);
        const errorMessage = err?.errors?.[0]?.message || err?.message || "Unknown error";
        setTasksErrorState(`Failed to load tasks: ${errorMessage}`);
      } finally {
        setTasksLoadingState(false);
        isFetchingRawItems.current = false;
      }
    };

    fetchRawItems();
  }, [rawItemsFetched, rawItems.length, personColumnId, personColumnIdMap, updateRawItems, setTasksLoadingState, setTasksErrorState]);

  // Update context when memoized values change (only runs when processed data actually changes)
  // processedTasksData is already memoized, so it only changes when dependencies change
  useEffect(() => {
    if (!rawItemsFetched || rawItems.length === 0) {
      return;
    }

    setTasksLoadingState(false);
    setTasksErrorState(null);

    // Update context with memoized processed data
    updatePropertyNameToIdMap(processedTasksData.propertyNameToIdMap);
    updateTasksByProperty(processedTasksData.tasksByProperty);
  }, [processedTasksData, rawItemsFetched, rawItems.length, updatePropertyNameToIdMap, updateTasksByProperty, setTasksLoadingState, setTasksErrorState]);

  // Update people in context when merged people list changes
  // Use a ref to track previous value and only update if content actually changed
  // This prevents infinite loops when availablePeople updates cause mergedPeople to recalculate
  const prevMergedPeopleStrRef = useRef(null);
  
  useEffect(() => {
    // Only update if mergedPeople has content
    if (mergedPeople.length === 0) {
      return;
    }
    
    // If availablePeople already has content from dropdown settings, prioritize it
    // Only update if dropdown options aren't loaded yet (use tasks as fallback)
    // OR if merged list has significantly more people (dropdown + tasks)
    if (availablePeople.length > 0) {
      // Dropdown options are loaded - only update if merged list has more people
      // This ensures dropdown options (all people) are preserved
      if (mergedPeople.length <= availablePeople.length) {
        // Dropdown options are comprehensive, don't overwrite
        return;
      }
    }
    
    // Compare by stringifying to avoid reference comparison issues
    const currentStr = JSON.stringify(mergedPeople);
    
    // Only update if the content actually changed (not just the reference)
    if (currentStr !== prevMergedPeopleStrRef.current) {
      updateAvailablePeople(mergedPeople);
      prevMergedPeopleStrRef.current = currentStr;
    }
  }, [mergedPeople, availablePeople, updateAvailablePeople]);

  const handleRefresh = useCallback(() => {
    // Reset the cache to force a fresh fetch from the API
    resetRawItemsCache();
    // Clear the fetching flag so the useEffect can run again
    isFetchingRawItems.current = false;
  }, [resetRawItemsCache]);

  return {
    loading: tasksLoading,
    error: tasksError,
    tasksByProperty,
    setTasksByProperty: updateTasksByProperty,
    propertyNameToIdMap,
    handleRefresh,
  };
}
